import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useScoresStrip, useFinalsForDate } from '../../hooks/useScoresStrip'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../lib/api'
import { getTeamLogoUrl, getTeamLogoFallbackUrl } from '../../lib/teamLogos'
import StatLeadersBlock from './StatLeadersBlock'
import GameCenterModal from '../picks/GameCenterModal'
import GameIntelModal from '../picks/GameIntelModal'
import { hasPregameIntel } from '../../lib/gameIntelSports'
import { formatLiveLabel } from '../../lib/liveLabel'
import { getNcaafMatchupScore } from '../../lib/ncaafPrestige'
import { sportsDayOf } from '../../lib/sportsDay'

// PT calendar date as YYYY-MM-DD — anchored to America/Los_Angeles so
// it matches the server's sports-day convention. Every US pro sport
// finishes within a PT calendar day, so bucketing by PT date is the
// convention the whole codebase uses (see server/src/utils/sportsDay).
function todayPT() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' })
}
function shiftPTDate(dateStr, days) {
  // Noon-anchored so DST transitions never flip the date.
  const d = new Date(`${dateStr}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}
function labelForPTDate(dateStr) {
  const today = todayPT()
  const yesterday = shiftPTDate(today, -1)
  if (dateStr === today) return 'Today'
  if (dateStr === yesterday) return 'Yesterday'
  // Parse as PT-noon so display isn't off by a day for East-Coast browsers.
  const d = new Date(`${dateStr}T12:00:00-07:00`)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' })
}

// Landing-page scores strip modeled loosely on Sleeper's homepage —
// four sport columns (NFL / MLB / WNBA / NBA) each showing what's live,
// what's coming up, and what just finished. Sits between Open Leagues
// and Status Tiers on the HomePage.
//
// Data comes from GET /api/scores/strip (public, no auth). The hook
// polls every 20s when any game is live, every 5min otherwise.
//
// This is Phase 1 of the landing revamp — scores only. Standings and
// stat leaders land in follow-up sessions; the sport-column shell here
// is the surface those will slot into.

const SPORTS = [
  { key: 'nfl', label: 'NFL', fullKey: 'americanfootball_nfl' },
  { key: 'ncaaf', label: 'NCAAF', fullKey: 'americanfootball_ncaaf' },
  { key: 'mlb', label: 'MLB', fullKey: 'baseball_mlb' },
  { key: 'wnba', label: 'WNBA', fullKey: 'basketball_wnba' },
  { key: 'nba', label: 'NBA', fullKey: 'basketball_nba' },
  { key: 'ncaab', label: 'NCAAB', fullKey: 'basketball_ncaab' },
  { key: 'mls', label: 'MLS', fullKey: 'soccer_usa_mls' },
]

// Grid class per active-sport count. Static classes (not built via
// string interpolation) so Tailwind's JIT picks them up at build
// time. Missing sport columns just get the row width redistributed
// instead of leaving a hollow slot for an offseason sport.
const GRID_CLASSES = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
  5: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
  6: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
  7: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4',
}

export default function SportsScoresStrip() {
  const { data, isLoading, error } = useScoresStrip()
  const isAuthenticated = useAuthStore((s) => !!s.session)
  // Mobile only: user picks a single sport via glass tabs below the
  // Scoreboard header instead of scrolling through all 4 stacked
  // columns. Desktop keeps the full grid.
  const [selectedMobileSport, setSelectedMobileSport] = useState(null)
  // Tapping any Final card opens an ESPN-style box score modal.
  const [gameCenterGameId, setGameCenterGameId] = useState(null)
  const [intelGameId, setIntelGameId] = useState(null)
  // Settled picks let us paint a green/red border on any Final game
  // the current user picked. Only fetch for logged-in users — no need
  // to burn a request on the public landing view. Uses useQuery
  // directly rather than useMyPicks so we can gate on `enabled` and
  // scope the cache key to the pick indicator use case.
  const { data: settledPicks } = useQuery({
    queryKey: ['scoreboardPickIndicators'],
    queryFn: () => api.get('/picks/me?status=settled'),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
    retry: false,
  })
  // Map<game_id, 'win' | 'loss' | 'push'>. A push/tie (is_correct = null
  // on a settled pick) surfaces with a yellow outline the same way it
  // does on the Results page — critical for MLS where regular-season
  // draws are common, and for anything else that can end tied.
  const pickOutcomeByGame = useMemo(() => {
    const map = new Map()
    for (const p of settledPicks || []) {
      if (!p.game_id || p.status !== 'settled') continue
      map.set(p.game_id, p.is_correct === true ? 'win' : p.is_correct === false ? 'loss' : 'push')
    }
    return map
  }, [settledPicks])

  // Only render sports with at least one game across any bucket. An
  // offseason sport (NBA in August) drops its column entirely so the
  // remaining columns widen to fill the row — no hollow slot.
  const activeSports = useMemo(() => {
    if (!data) return []
    return SPORTS.filter((s) => {
      const col = data[s.key]
      return (col?.live?.length || 0) + (col?.upcoming?.length || 0) + (col?.recent?.length || 0) > 0
    })
  }, [data])

  if (isLoading) return null
  if (error) return null
  if (!activeSports.length) return null

  const gridClass = GRID_CLASSES[activeSports.length] || GRID_CLASSES[4]

  return (
    // xl:-mx-24 mirrors the Status Tiers section below — pulls the
    // strip wider than the default container at desktop breakpoints
    // so 4 sport columns get real breathing room instead of getting
    // squeezed into ~half the page width.
    <section className="mb-10 xl:-mx-24" data-onboarding="scoreboard">
      <h2 className="font-display text-2xl mb-4">Scoreboard</h2>

      {/* Mobile-only sport tabs — glass-styled pills. Stacking all 4
          sport columns vertically made mobile users scroll past NFL
          just to see NBA scores; picking a sport up-front is faster
          and matches how Sleeper does it. Desktop gets its own tab
          row below. */}
      {activeSports.length > 1 && (
        <div className="sm:hidden flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          {activeSports.map((sport) => {
            const isActive = (selectedMobileSport || activeSports[0].key) === sport.key
            return (
              <button
                key={sport.key}
                onClick={() => setSelectedMobileSport(sport.key)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold backdrop-blur-md transition-colors border ${
                  isActive
                    ? 'bg-accent/20 border-accent text-text-primary'
                    : 'bg-bg-primary/40 border-text-primary/15 text-text-secondary hover:text-text-primary hover:border-text-primary/30'
                }`}
              >
                {sport.label}
              </button>
            )
          })}
        </div>
      )}

      {/* Desktop-only sport tabs — TOP shows the multi-column overview
          (default view), individual sport pills navigate to the sport
          drill-in page for a wider, richer single-sport view. Only
          renders when there are 2+ sports to switch between. */}
      {activeSports.length > 1 && (
        <div className="hidden sm:flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          <button
            disabled
            className="shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold backdrop-blur-md border bg-accent/20 border-accent text-text-primary"
          >
            TOP
          </button>
          {activeSports.map((sport) => (
            <Link
              key={sport.key}
              to={`/scores/${sport.key}`}
              className="shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold backdrop-blur-md transition-colors border bg-bg-primary/40 border-text-primary/15 text-text-secondary hover:text-text-primary hover:border-text-primary/30"
            >
              {sport.label}
            </Link>
          ))}
        </div>
      )}

      <div className={`grid gap-4 ${gridClass}`}>
        {activeSports.map((sport) => {
          const effectiveMobileSport = selectedMobileSport || activeSports[0].key
          const showOnMobile = sport.key === effectiveMobileSport
          return (
            <div key={sport.key} className={showOnMobile ? '' : 'hidden sm:block'}>
              <SportColumn
                sport={sport}
                data={data[sport.key]}
                pickOutcomeByGame={pickOutcomeByGame}
                onOpenGameCenter={setGameCenterGameId}
                onOpenIntel={setIntelGameId}
              />
            </div>
          )
        })}
      </div>
      <GameCenterModal gameId={gameCenterGameId} onClose={() => setGameCenterGameId(null)} />
      <GameIntelModal gameId={intelGameId} onClose={() => setIntelGameId(null)} />
    </section>
  )
}

function SportColumn({ sport, data, pickOutcomeByGame, onOpenGameCenter, onOpenIntel }) {
  // Parent filters out sports with zero games across all buckets before
  // mapping, so we're guaranteed at least one bucket has rows here.
  let live = data?.live || []
  let upcoming = data?.upcoming || []
  let recent = data?.recent || []

  // NCAAF: sort each bucket by matchup importance (combined AP rank
  // ascending, prestige tiebreak) so marquee games float to the top.
  // Ranked-vs-ranked → ranked-vs-unranked → unranked-vs-unranked.
  if (sport.key === 'ncaaf') {
    const cmp = (a, b) => getNcaafMatchupScore(a) - getNcaafMatchupScore(b)

    // Slate day FIRST, then importance within the day.
    //
    // Importance alone ignored the calendar entirely, so #10 Oklahoma vs #3
    // Georgia on Sep 26 sat above four Sep 12 games — a scoreboard showing a
    // game three weekends out before this Saturday's. College schedules
    // release weeks ahead, so the effect compounds the further out the slate
    // goes, and finals had the mirror problem: an older marquee game
    // outranking last night's.
    //
    // Day buckets rather than a continuous recency penalty: a Saturday slate
    // is the unit fans think in, everything within one weekend still ranks by
    // quality, and there is no weighting constant to tune.
    //
    // Unknown days sink to the bottom in both directions rather than riding
    // the '' sort order, which would float an undated game to the very top of
    // upcoming.
    const byDay = (newestFirst) => (a, b) => {
      const da = sportsDayOf(a.starts_at)
      const db = sportsDayOf(b.starts_at)
      if (da !== db) {
        if (!da) return 1
        if (!db) return -1
        return (newestFirst ? da > db : da < db) ? -1 : 1
      }
      return cmp(a, b)
    }

    // Live keeps pure importance: they are all happening now, so the calendar
    // says nothing useful about them.
    live = [...live].sort(cmp)
    upcoming = [...upcoming].sort(byDay(false))
    recent = [...recent].sort(byDay(true))
  }

  return (
    <div>
      <div className="w-full py-2 mb-2 flex items-center justify-between">
        {/* Sport label doubles as the drill-in link. Mobile no longer
            needs a per-column collapse toggle — the sport tabs above
            already gate visibility to one sport at a time. */}
        <Link to={`/scores/${sport.key}`} className="font-display text-lg text-text-primary hover:text-accent transition-colors flex items-center gap-1 group">
          {sport.label}
          <svg className="w-4 h-4 text-text-muted group-hover:text-accent transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </Link>
      </div>
      {/* Order: Live → Final → Upcoming → Leaders (top 3). Recent
          finals slot above upcoming so a user landing on a sport with
          a completed slate sees 'what just happened' first — matches
          Sleeper's action-first hierarchy.
          Final section only shows when there's at least one final
          from TODAY. Yesterday's finals live behind the drill-in
          date scrubber — no need to render an empty Final block on
          today when nothing has completed yet. */}
      {(() => {
        const todayStr = todayPT()
        const todayRecent = recent.filter((g) => sportsDayOf(g.starts_at) === todayStr)
        return (
          <div className="space-y-4">
            {live.length > 0 && (
              <BucketSection label="Live" games={live} sportFullKey={sport.fullKey} isLive onOpenGameCenter={onOpenGameCenter} onOpenIntel={onOpenIntel} />
            )}
            {todayRecent.length > 0 && (
              <FinalSection sport={sport} todayRecent={todayRecent} pickOutcomeByGame={pickOutcomeByGame} onOpenGameCenter={onOpenGameCenter} onOpenIntel={onOpenIntel} />
            )}
            {upcoming.length > 0 && (
              <BucketSection label={live.length > 0 ? 'Coming up' : 'Upcoming'} games={upcoming} sportFullKey={sport.fullKey} onOpenIntel={onOpenIntel} />
            )}
            <StatLeadersBlock sport={sport.key} mode="compact" />
          </div>
        )
      })()}
    </div>
  )
}

// Final section with a per-sport date navigator. Default is "today"
// which uses the games already in the strip payload — free, no fetch.
// Tapping the left arrow steps back one PT day and lazy-fetches that
// day's finals via /api/scores/finals. Right arrow disabled once we're
// back at today so users can't scroll into the future here.
function FinalSection({ sport, todayRecent, pickOutcomeByGame, onOpenGameCenter, onOpenIntel }) {
  const today = todayPT()
  const [date, setDate] = useState(today)
  const isToday = date === today
  // Always fetch via the date-scoped endpoint so today and yesterday
  // never overlap. Previously "today" reused the strip's last-30h
  // recent bucket, which at 12am–3am PT bleeds heavily into
  // yesterday's PT games — user saw the same finals under both
  // "today" and "yesterday" arrows.
  const { data: fetched, isLoading } = useFinalsForDate(sport.key, date)
  // Fall back to strip's recent bucket on the initial paint before
  // useFinalsForDate returns, so today's card doesn't flicker empty.
  const games = fetched ?? (isToday ? todayRecent : [])

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Final</span>
        <div className="flex items-center gap-1 ml-auto">
          <button
            type="button"
            onClick={() => setDate((d) => shiftPTDate(d, -1))}
            className="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
            aria-label="Previous day"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="text-[11px] font-semibold text-text-secondary min-w-[52px] text-center tabular-nums">
            {labelForPTDate(date)}
          </span>
          <button
            type="button"
            onClick={() => setDate((d) => shiftPTDate(d, 1))}
            disabled={isToday}
            className="w-5 h-5 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
            aria-label="Next day"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        {isLoading && !isToday ? (
          <div className="rounded-lg border border-text-primary/10 bg-bg-primary/20 backdrop-blur-md px-4 py-3 text-xs text-text-muted">Loading…</div>
        ) : games.length === 0 ? (
          <div className="rounded-lg border border-text-primary/10 bg-bg-primary/20 backdrop-blur-md px-4 py-3 text-xs text-text-muted">No games</div>
        ) : (
          games.map((g) => (
            <GameCard
              key={g.id}
              game={g}
              sportFullKey={sport.fullKey}
              isFinal
              pickOutcome={pickOutcomeByGame?.get(g.id)}
              onOpenGameCenter={onOpenGameCenter}
              onOpenIntel={onOpenIntel}
            />
          ))
        )}
      </div>
    </div>
  )
}

function BucketSection({ label, games, sportFullKey, isLive, isFinal, onOpenGameCenter, onOpenIntel }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        {isLive && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${isLive ? 'text-red-400' : 'text-text-muted'}`}>
          {label}
        </span>
      </div>
      <div className="space-y-1.5">
        {games.map((g) => (
          <GameCard key={g.id} game={g} sportFullKey={sportFullKey} isLive={isLive} isFinal={isFinal} onOpenGameCenter={onOpenGameCenter} onOpenIntel={onOpenIntel} />
        ))}
      </div>
    </div>
  )
}

function GameCard({ game, sportFullKey, isLive, isFinal, pickOutcome, onOpenGameCenter, onOpenIntel }) {
  // Live + Final rows show the score inline next to each team.
  // Upcoming rows show a time/date pill on the right instead.
  // Sleeper-style: each matchup is its own subtle bordered card,
  // no outer container wrapping the sport list.
  //
  // Final games the user picked get a green (correct) or red (wrong)
  // outline + faint tint (win = green, loss = red, push/tie = yellow).
  // Only applied when pickOutcome is set (undefined = no pick / not
  // authenticated) and we're in isFinal mode — Live/Upcoming don't
  // render the outline.
  const showScore = isLive || isFinal
  const hasPick = isFinal && !!pickOutcome
  const outlineClass = hasPick
    ? pickOutcome === 'win'
      ? 'border-correct/60 bg-correct/5'
      : pickOutcome === 'loss'
        ? 'border-incorrect/60 bg-incorrect/5'
        : 'border-yellow-500/60 bg-yellow-500/5'
    : 'border-text-primary/10 bg-bg-primary/20'
  // Live game clock / inning label — Sleeper/ESPN-style "Bot 7th" for
  // MLB, "Q3 · 4:32" for football/basketball. Data comes from ESPN
  // via syncLiveScores (period + clock columns on games).
  const liveLabel = isLive ? formatLiveLabel(game.period, game.clock, sportFullKey) : null
  // Live/final only. The pre-game preview lives on the Picks page's Game
  // Intel modal instead — opening Game Center from the scoreboard before
  // kickoff was more surface than it earned.
  // Upcoming games open the pregame Game Intel modal where we have one;
  // live/final open Game Center. Same routing as the drill-in scoreboard.
  const hasIntel = !isLive && !isFinal && hasPregameIntel(sportFullKey) && !!onOpenIntel
  const tappable = ((isFinal || isLive) && !!onOpenGameCenter) || hasIntel
  return (
    <div
      onClick={tappable ? () => (hasIntel ? onOpenIntel(game.id) : onOpenGameCenter(game.id)) : undefined}
      className={`rounded-lg border backdrop-blur-md px-4 py-2.5 ${outlineClass} ${tappable ? 'cursor-pointer hover:bg-bg-primary/30 transition-colors' : ''}`}>
      {/* Live-state header (Bot 7th / Q3 · 4:32) OR MLB R/H/E header.
          Both live in the same top row: state on the left, R/H/E on
          the right, so a live MLB game shows "Bot 7th" + R H E. */}
      {(liveLabel || game.linescore) && (
        <div className="flex items-center justify-between gap-2 mb-1">
          {liveLabel ? (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-red-400">{liveLabel}</span>
          ) : <span />}
          {game.linescore && (
            <div className="flex gap-1">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted w-6 text-center">R</span>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted w-6 text-center">H</span>
              <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted w-6 text-center">E</span>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0 space-y-1.5">
        <TeamRow
          team={game.away_short || game.away_team}
          logoLookupTeam={game.away_team}
          record={game.away_record}
          score={showScore ? game.away_score : null}
          hits={game.linescore?.away?.h}
          errors={game.linescore?.away?.e}
          sportFullKey={sportFullKey} isLive={isLive}
          rank={game.away_rank}
        />
        <TeamRow
          team={game.home_short || game.home_team}
          logoLookupTeam={game.home_team}
          record={game.home_record}
          score={showScore ? game.home_score : null}
          hits={game.linescore?.home?.h}
          errors={game.linescore?.home?.e}
          sportFullKey={sportFullKey} isLive={isLive}
          rank={game.home_rank}
        />
      </div>
      {!showScore && (
        <div className="shrink-0">
          <TimeBox startsAt={game.starts_at} />
        </div>
      )}
      </div>
    </div>
  )
}

function TeamRow({ team, logoLookupTeam, record, score, hits, errors, sportFullKey, isLive, rank }) {
  // Logo helper needs the FULL name (Detroit Lions, San Francisco
  // Giants) since it's keyed by full name in the abbreviation map.
  // Display name is the short version passed via `team`.
  const logoUrl = getTeamLogoUrl(logoLookupTeam || team, sportFullKey)
  const fallbackUrl = getTeamLogoFallbackUrl(logoLookupTeam || team, sportFullKey)
  return (
    <div className="flex items-center gap-2.5">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          width="28" height="28"
          className="w-7 h-7 object-contain shrink-0"
          loading="lazy"
          onError={(e) => {
            if (fallbackUrl && e.currentTarget.src !== fallbackUrl) e.currentTarget.src = fallbackUrl
            else e.currentTarget.style.visibility = 'hidden'
          }}
        />
      ) : (
        <div className="w-7 h-7 rounded-full bg-bg-secondary shrink-0" />
      )}
      {/* Team name + record share a row that hugs the left. Sleeper
          renders "Red Sox 64-53" with the record right next to the
          team name (not right-aligned), which reads as one unit
          instead of two separate columns. */}
      <div className="flex-1 min-w-0 flex items-baseline gap-2">
        {rank && <span className="text-sm font-bold text-accent tabular-nums shrink-0">#{rank}</span>}
        <span className="text-sm text-text-primary truncate">{team}</span>
        {record && (
          <span className="text-[11px] text-text-muted tabular-nums shrink-0">{record}</span>
        )}
      </div>
      {/* MLB R/H/E: fixed-width cells matching the header widths above
          so 12 hits doesn't shove E out of column. R bold, H/E lighter. */}
      {score != null && (hits != null || errors != null) ? (
        <div className="flex items-baseline gap-1 shrink-0 tabular-nums">
          <span className={`w-6 text-center text-sm font-semibold ${isLive ? 'text-text-primary' : 'text-text-secondary'}`}>{score}</span>
          <span className="w-6 text-center text-[11px] text-text-muted">{hits != null ? hits : ''}</span>
          <span className="w-6 text-center text-[11px] text-text-muted">{errors != null ? errors : ''}</span>
        </div>
      ) : score != null ? (
        <div className={`text-sm font-semibold tabular-nums shrink-0 ${isLive ? 'text-text-primary' : 'text-text-secondary'}`}>
          {score}
        </div>
      ) : null}
    </div>
  )
}

// Format the live-game state label. MLB's period from ESPN is already
// a friendly string ("Top 5th" / "Bot 7th" / "Mid 3rd"), so we pass
// it through. For football / basketball, period is a quarter number
// and clock is a display clock like "4:32" — join as "Q3 · 4:32".
// For soccer, period 1 = first half, 2 = second half, 3 = extra time;
// clock renders as "45'+2" style — join as "1H · 34'".

function TimeBox({ startsAt }) {
  if (!startsAt) return null
  const d = new Date(startsAt)
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const isToday = new Date().toDateString() === d.toDateString()
  return (
    <div className="text-[11px] text-text-muted leading-tight text-right">
      <div className="font-semibold text-text-secondary">{isToday ? 'Today' : day}</div>
      <div>{time}</div>
    </div>
  )
}
